/**
 * Catalog card visibility (Phase: /admin/products card overrides).
 *
 * Card-level visibility plane that composes with the per-SKU flags on
 * catalog_skus. A card is hidden on a surface when EITHER:
 *   1. The card-level toggle is off in this table, OR
 *   2. The SKU rollup says hide (every variant Inactive / not marketing_web,
 *      or any variant is_draft — owned by @insolech/sdk catalogSkuQueries).
 *
 * "Default to visible" — an absent row means the card is visible on
 * both surfaces and not a draft. Admin toggles upsert a row only when
 * they need to override the default.
 *
 * RLS:
 *   - SELECT: any anon or authenticated user.
 *   - INSERT/UPDATE/DELETE: admin only.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { readViteEnv } from '../util/vite-env';

export interface CatalogCardVisibility {
  card_code: string;
  family: string;
  web_app_enabled: boolean;
  marketing_web_enabled: boolean;
  is_draft: boolean;
  updated_at: string;
  updated_by: string | null;
}

/**
 * Patch shape for upserts. Only the three toggle columns can be edited;
 * `card_code` is the primary key (set on insert), `family` is set on
 * insert and not mutated thereafter, and `updated_at` / `updated_by`
 * are maintained by the DB / RLS.
 */
export type CatalogCardVisibilityPatch = Partial<
  Pick<
    CatalogCardVisibility,
    'web_app_enabled' | 'marketing_web_enabled' | 'is_draft'
  >
>;

export const catalogCardVisibilityKeys = {
  all: () => ['catalog_card_visibility'] as const,
  list: () => ['catalog_card_visibility', 'list'] as const,
  byCodes: (codes: readonly string[]) =>
    ['catalog_card_visibility', 'by_codes', [...codes].sort()] as const,
};

/** Full list — used by the admin /admin/products card grid. ~121 cards max,
 *  but in practice only overridden cards are stored (table is sparse). */
export function catalogCardVisibilityListQuery(client: SupabaseClient) {
  return {
    queryKey: catalogCardVisibilityKeys.list(),
    queryFn: async (): Promise<CatalogCardVisibility[]> => {
      const { data, error } = await client
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('catalog_card_visibility' as any)
        .select('*')
        .order('family', { ascending: true })
        .order('card_code', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as CatalogCardVisibility[];
    },
  };
}

/**
 * Batch lookup by card code — used by the marketing loader to compose
 * card-level overrides into the SKU rollup. Returns a Map keyed by code
 * for O(1) lookup at render time. Missing rows are absent from the map
 * (caller treats absent as "default visible").
 */
export function catalogCardVisibilityByCodeQuery(
  client: SupabaseClient,
  codes: readonly string[],
) {
  return {
    queryKey: catalogCardVisibilityKeys.byCodes(codes),
    queryFn: async (): Promise<Map<string, CatalogCardVisibility>> => {
      if (codes.length === 0) return new Map();
      const { data, error } = await client
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('catalog_card_visibility' as any)
        .select('*')
        .in('card_code', codes as string[]);
      if (error) throw error;
      const map = new Map<string, CatalogCardVisibility>();
      for (const row of (data ?? []) as unknown as CatalogCardVisibility[]) {
        map.set(row.card_code, row);
      }
      return map;
    },
  };
}

// =============================================================================
// Marketing revalidation coalescer — same pattern as catalog-skus.ts.
//
// When an admin toggles a card-level visibility flag we want insolech.com to
// reflect the change within seconds, not after the 5-min ISR window expires.
// Fire-and-forget POST to VITE_MARKETING_REVALIDATE_URL. Failures swallowed.
// Coalesced into at most 2 fetches (leading-edge + trailing-edge) so a bulk
// action over N cards collapses to 2 cache purges.
// =============================================================================
let revalidationInFlight: Promise<void> | null = null;
let revalidationPending = false;

async function triggerMarketingRevalidation(): Promise<void> {
  if (revalidationInFlight) {
    revalidationPending = true;
    return;
  }
  revalidationInFlight = revalidationFetchOnce().finally(() => {
    revalidationInFlight = null;
    if (revalidationPending) {
      revalidationPending = false;
      void triggerMarketingRevalidation();
    }
  });
  return revalidationInFlight;
}

async function revalidationFetchOnce(): Promise<void> {
  // Same literal-inline env read as catalog-skus (see readViteEnv): inlined in
  // the browser bundle, undefined (skip) in Node/Deno.
  const url = readViteEnv('VITE_MARKETING_REVALIDATE_URL');
  const secret = readViteEnv('VITE_REVALIDATE_SECRET');
  if (!url || !secret) return;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'x-revalidate-secret': secret,
        'content-type': 'application/json',
      },
      body: '{}',
      keepalive: true,
    });
    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.warn(
        '[catalog-card-visibility] marketing revalidation responded',
        res.status,
        await res.text().catch(() => ''),
      );
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[catalog-card-visibility] marketing revalidation failed', err);
  }
}

/**
 * Upsert a single card's visibility row. Inserts a new row with the patch
 * applied over defaults, or updates the existing row in place. The DB trigger
 * maintains `updated_at`. `updated_by` is set from the calling client's
 * auth context when available.
 *
 * Fires triggerMarketingRevalidation() on success (coalesced).
 */
export async function upsertCatalogCardVisibility(
  client: SupabaseClient,
  code: string,
  family: string,
  patch: CatalogCardVisibilityPatch,
): Promise<void> {
  // Look up the calling user so updated_by reflects the admin who toggled.
  // If auth context is unavailable (service-role / tests) we leave it null.
  //
  // We deliberately use getSession() (synchronous read from local storage)
  // instead of getUser() — getUser() makes a network round-trip to
  // /auth/v1/user and has been observed to hang indefinitely in the same
  // environments that triggered the noopLock workaround in createBrowserClient
  // (browser extensions tampering with the Locks API, broken service workers).
  // A hang here would silently swallow the toggle click because the await
  // before upsert() never resolves, producing the exact "toggle does nothing,
  // no network request" symptom.
  let updatedBy: string | null = null;
  try {
    const { data } = await client.auth.getSession();
    updatedBy = data?.session?.user?.id ?? null;
  } catch {
    updatedBy = null;
  }
  const row = {
    card_code: code,
    family,
    ...patch,
    ...(updatedBy ? { updated_by: updatedBy } : {}),
  };
  const { error } = await client
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .from('catalog_card_visibility' as any)
    .upsert(row as Record<string, unknown>, { onConflict: 'card_code' });
  if (error) throw error;
  void triggerMarketingRevalidation();
}

/**
 * Bulk upsert — applies a row per (code, family, patch). Used by the
 * bulk-actions toolbar so toggling N cards collapses to one round trip
 * and one (coalesced) marketing revalidation.
 */
export async function bulkUpsertCatalogCardVisibility(
  client: SupabaseClient,
  updates: ReadonlyArray<{
    code: string;
    family: string;
    patch: CatalogCardVisibilityPatch;
  }>,
): Promise<void> {
  if (updates.length === 0) return;
  // See note on getSession() vs getUser() in upsertCatalogCardVisibility above.
  let updatedBy: string | null = null;
  try {
    const { data } = await client.auth.getSession();
    updatedBy = data?.session?.user?.id ?? null;
  } catch {
    updatedBy = null;
  }
  const rows = updates.map((u) => ({
    card_code: u.code,
    family: u.family,
    ...u.patch,
    ...(updatedBy ? { updated_by: updatedBy } : {}),
  }));
  const { error } = await client
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .from('catalog_card_visibility' as any)
    .upsert(rows as Record<string, unknown>[], { onConflict: 'card_code' });
  if (error) throw error;
  void triggerMarketingRevalidation();
}
